/**
 * Shared Lilac class strings for the Classic pages that were restyled onto the
 * token layer (src/styles/tokens.css, DESIGN.md) without a structural rewrite.
 * Every value is built from `g-*` utilities only, so the same markup renders in
 * Classic, Lilac and Lilac dark without a dark-mode branch.
 */

/** Column card: 20 px radius, one-pixel line, white, card shadow. */
export const lilacCard = "rounded-g-xl border border-g-line bg-g-bg-card shadow-g-card";

/** Card header padding from the design system ("card-pad": 18px 20px). */
export const lilacCardHeader = "p-[18px_20px]";

/** Card body below a Lilac header: the same 20 px sides, no top padding. */
export const lilacCardContent = "px-5 pb-5 pt-0";

/** Card heading: the "title" role (15 px / 700, ink 900). */
export const lilacCardTitle = "text-[15px] font-bold text-g-ink-900";

/** Page heading, matching PeopleView's h1. */
export const lilacPageTitle = "text-[28px] font-extrabold tracking-[-0.02em] text-g-ink-900";

/** Sub-page or section heading: the "headline" role at 700. */
export const lilacHeadline = "text-[19px] font-bold tracking-[-0.01em] text-g-ink-900";

/** The line under a page heading. */
export const lilacSubtitle = "text-[15px] text-g-ink-600";

/** Secondary text. */
export const lilacMuted = "text-g-ink-500";

/** Primary button: purple on white, button glow, hover to primary-hover. */
export const lilacPrimaryBtn =
  "h-10 rounded-g-md bg-g-primary px-4 text-[14px] font-semibold text-g-on-primary shadow-g-button hover:bg-g-primary-hover";

/** Secondary button: white with a line, ink text, hover to the primary wash. */
export const lilacSecondaryBtn =
  "h-10 rounded-g-md border border-g-line bg-g-bg-card px-4 text-[14px] font-semibold text-g-ink-900 shadow-g-card hover:bg-g-primary-wash";

/** Text input: 40 px, white, one-pixel line, 10 px radius. */
export const lilacInput =
  "h-10 rounded-g-sm border border-g-line bg-g-bg-card px-3 text-[14px] text-g-ink-900 placeholder:text-g-ink-400";

/** Multi-line text field: the input's surface without the fixed height. */
export const lilacTextarea =
  "rounded-g-sm border border-g-line bg-g-bg-card px-3 py-2 text-[14px] text-g-ink-900 placeholder:text-g-ink-400";

export type LilacPillTone = "success" | "warning" | "danger" | "info" | "neutral" | "primary";

/* The hover resets neutralise the shadcn Badge variants' own hover fills. */
const PILL_TONES: Record<LilacPillTone, string> = {
  success: "bg-g-success-tint text-g-success hover:bg-g-success-tint",
  warning: "bg-g-warning-tint text-g-warning hover:bg-g-warning-tint",
  danger: "bg-g-danger-tint text-g-danger hover:bg-g-danger-tint",
  info: "bg-g-info-tint text-g-info hover:bg-g-info-tint",
  neutral: "bg-g-bg-track text-g-ink-500 hover:bg-g-bg-track",
  primary: "bg-g-primary-tint text-g-primary-press hover:bg-g-primary-tint",
};

/** Status pill: the status colour as text on its own tint, never a saturated fill. */
export function lilacPill(tone: LilacPillTone): string {
  return `inline-flex items-center rounded-g-pill border border-transparent px-2 py-0.5 text-xs font-semibold ${PILL_TONES[tone]}`;
}
